import React, { Component } from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import { filter, find, forEach, get, head } from 'lodash';
import { CONFIRM_BUTTON_STATES, CONTEXT, MODES } from 'constants';
import mediator, { ACTIONS } from 'mediator';

import API from 'lib/tessitura/API';
import logger from 'lib/common/logger';
import fetcher from 'lib/common/fetcher';
import features from 'lib/common/features';
import arrayWrap from 'lib/common/arrayWrap';
import packages from 'lib/cart/packages';
import panToElement from 'lib/seatmaps/panToElement';
import preselectLevel from 'lib/seatmaps/preselectLevel';
import preselectScreen from 'lib/seatmaps/preselectScreen';
import generateSeatMap from 'lib/seatmaps/generateSeatMap';
import combineSubLineItems from 'lib/cart/combineSubLineItems';
import filterPastPerformances from 'lib/common/filterPastPerformances';
import fetchSeatViews from 'lib/seats/fetchSeatViews';
import { redirectWithError } from 'lib/common/fallbacks';

/**
 * Container components
 */
import SeatMapContainer from 'containers/SeatMapContainer';
import DetailsContainer from 'containers/DetailsContainer';

import ExpirationTimer from 'components/ExpirationTimer';
import { findSeatEl } from '../lib/seats/seatFinders';
import Modal from 'components/Modal';
import ReactToolTip from 'react-tooltip';

class SYOSContainer extends Component {
  constructor(props) {
    super(props);

    this.state = {
      currentPerformanceId: props.initialPerformanceId,
      currentCart: null,
      currentCartItems: null,
      currentFixedPackage: null,
      currentFlexPackage: null,
      selectedSeats: [],
      isFixedPackagePerformance: false,
      maxTickets: props.maxTickets,
      svgData: null,
      performances: [],
      levels: [],
      zonesByLevel: null,
      seatViews: null,
      isEnhanced: false,
      levelSelected: false,
      selectedScreenId: '1',
      panZoomInstance: null,
      showMessage: false
    };

    this.getMode = this.getMode.bind(this);
    this.onChangePerformance = this.onChangePerformance.bind(this);
    this.fetchSeatMap = this.fetchSeatMap.bind(this);
    this.fetchSeatViews = this.fetchSeatViews.bind(this);
    this.fetchPerformance = this.fetchPerformance.bind(this);
    this.onScreenSelected = this.onScreenSelected.bind(this);
    this.onSeatMapReady = this.onSeatMapReady.bind(this);
    this.onPanZoomInit = this.onPanZoomInit.bind(this);
    this.renderNotice = this.renderNotice.bind(this);
    this.setSelectedSeats = this.setSelectedSeats.bind(this);
    this.handleRelease = this.handleRelease.bind(this);
  }

  componentDidMount() {
    this.fetchSeatMap(this.props.loginInfo).then(() => {
      let performanceId = this.state.currentPerformanceId;
      this.fetchPerformance(performanceId);
    });

    this.fetchSeatViews();
    ReactToolTip.rebuild();
  }

  componentWillReceiveProps(nextProps) {
    let shouldGenerateSeatMap =
      !this.state.isEnhanced &&
      nextProps.usesGeneratedSeatmap &&
      nextProps.seatData.length;

    if (shouldGenerateSeatMap) {
      let svgData;
      try {
        svgData = generateSeatMap(nextProps.seatData);
      } catch (e) {
        redirectWithError(new Error('Failed to create seat-map'));
      }

      this.setState({
        svgData: svgData
      });
    }
  }

  handleRelease(cb) {
    this.setState({
      buttonState: CONFIRM_BUTTON_STATES.CONFIRMING
    });

    API.getCart()
      .then(cartData => {
        let promises = [];
        if (this.props.packageNo > 0) {
          let lineItems = filter(cartData.PackageLineItem, item => {
            return item.pkg_no === this.props.packageNo;
          });

          forEach(lineItems, item => {
            promises.push(
              API.ReleaseTickets(
                this.props.packageNo,
                item.li_no,
                this.props.packageNo
              )
            );
          });
        } else {
          let lineItems = filter(cartData.LineItem, item => {
            return item.perf_no === this.state.currentPerformanceId;
          });

          forEach(lineItems, item => {
            promises.push(
              API.ReleaseTickets(this.state.currentPerformanceId, item.li_no)
            );
          });
        }

        Promise.all(promises)
          .then(() => {
            cb();
            this.props.updateContext(CONTEXT.SECTION_SELECTION, true);
          })
          .catch(() => {
            cb();

            let url = document.location.href;
            let currentPage = url.split('?');
            window.location = currentPage[0];
          });
      })
      .catch(() => {
        cb();

        let url = document.location.href;
        let currentPage = url.split('?');
        window.location = currentPage[0];
      });
  }

  getMode() {
    let isFixedPackage = !!this.props.packageNo;
    let isFixedPackagePerformance = !!this.state.isFixedPackagePerformance;
    let isFlexPackagePerformance = !!this.state.currentFlexPackage;

    let mode;

    if (isFixedPackage) {
      mode = MODES.FIXED_PACKAGE;
    } else if (isFixedPackagePerformance) {
      mode = MODES.FIXED_PACKAGE_PERFORMANCE;
    } else if (isFlexPackagePerformance) {
      mode = MODES.FLEX_PACKAGE_PERFORMANCE;
    } else {
      mode = MODES.PERFORMANCE;
    }

    return mode;
  }

  onChangePerformance(performanceId) {
    this.props.onChangePerformance(performanceId);

    // Store a reference to previous panZoom state
    let oldPanZoom = this.state.panZoomInstance;

    if (!oldPanZoom) {
      return;
    }

    let currentPan = oldPanZoom.getPan();
    let currentZoom = oldPanZoom.getZoom();

    /**
     * Reset only the state that needs to
     * change when switching performance.
     */
    this.setState({
      currentPerformanceId: performanceId,
      currentCart: null,
      currentCartItems: null,
      svgData: null,
      zonesByLevel: null,
      seatViews: null,
      isEnhanced: false,
      selectedScreenId: null,
      panZoomInstance: null
    });

    /**
     * Re-fetch seat-map.
     *
     * As we modify the seat-map DOM the simplest way to
     * reset it is to completely re-render the seat-map.
     *
     * This is quick so it preferable to the complex
     * state-management needed to reset the existing seat-map.
     */
    this.fetchSeatMap(this.props.loginInfo).then(() => {
      this.fetchSeatViews();
      this.fetchPerformance(performanceId);

      // Reset pan position and zoom level.
      let newPanZoom = this.state.panZoomInstance;
      if (newPanZoom) {
        newPanZoom.zoom(currentZoom);
        newPanZoom.pan(currentPan);
      }
    });
  }

  fetchSeatMap() {
    if (this.props.currentFacility) {
      return fetcher.fetchHtml(this.props.currentFacility.svg).then(svgData => {
        this.setState({
          svgData: svgData,
          levelSelected: true,
          seatMapReady: true
        });

        return svgData;
      });
    } else {
      return Promise.resolve(null);
    }
  }

  fetchSeatViews() {
    let endpoint = features.getOption('viewFromSeat', 'endpoint');
    if (endpoint) {
      fetchSeatViews(endpoint, this.props.currentFacility).then(seatViews => {
        if (seatViews) {
          this.setState({ seatViews: seatViews });
        }
      });
    }
  }

  fetchPerformance(performanceId) {
    let { loginInfo } = this.props;

    /**
     * We only need a list of all available performances
     * in a run if the performance selector is enabled.
     * If the feature is disabled we can save an API call.
     */
    if (features.isEnabled('performanceSelector')) {
      API.getProductionDetail(performanceId, loginInfo.MOS).then(result => {
        let performance = get(result, 'Performance');
        if (!performance) {
          performance = get(result, 'result');
        }
        let performances = arrayWrap(performance);
        if (performances.length) {
          this.setState({
            performances: filterPastPerformances(performances)
          });
        }
      });
    }
  }

  onScreenSelected(screenId) {
    this.setState({
      levelSelected: !this.state.levelSelected ? true : false,
      selectedScreenId: screenId
    });

    mediator.publish(ACTIONS.SCREEN_FILTER_SELECTED, {
      selected: screenId
    });
  }

  onSeatMapReady(collectedSeats, zonesByLevel, levels) {
    const perfNo = this.state.currentPerformanceId;
    const packageNo = this.props.packageNo;

    let states = {
      isEnhanced: true
    };

    if (zonesByLevel) {
      states.zonesByLevel = zonesByLevel;
    }
    if (levels) {
      states.levels = levels;
    }

    this.setState(states);

    /**
     * Fetch existing cart
     * TODO: Could this happen earlier?
     */
    (this.props.getCartPromise !== null
      ? this.props.getCartPromise
      : API.getCart()
    ).then(
      currentCart => {
        let cart = currentCart || null;
        let cartItems = cart
          ? combineSubLineItems(cart, perfNo, packageNo)
          : [];
        let packageItems = arrayWrap(get(cart, 'PackageLineItem'));

        if (cartItems.length === 0) {
          this.setState({
            showMessage: true
          });

          let url = document.location.href;
          let currentPage = url.split('?');
          window.location = currentPage[0];
          return;
        }

        let currentFixedPackage = null;
        if (packageItems.length) {
          currentFixedPackage = packages.getCurrentFixedPackage(
            packageItems,
            packageNo
          );
        }

        let currentFlexPackage = null;
        if (packageItems.length && perfNo) {
          currentFlexPackage = packages.getCurrentFlexPackage(
            packageItems,
            perfNo
          );
        }

        let isFixedPackagePerformance = false;
        if (packageItems.length && perfNo) {
          let fixedPackage = packages.getCurrentFixedPackagePerf(packageItems);
          isFixedPackagePerformance = !!fixedPackage;
        }

        this.setState({
          currentCart: cart,
          currentCartItems: cartItems,
          currentFixedPackage: currentFixedPackage,
          currentFlexPackage: currentFlexPackage,
          isFixedPackagePerformance: isFixedPackagePerformance
        });

        if (this.props.usesGeneratedSeatmap && this.props.screenZoneData) {
          let selectedScreenNo = preselectScreen(
            cartItems,
            this.props.screenZoneData
          );

          if (selectedScreenNo) {
            this.onScreenSelected(selectedScreenNo);
          }

          this.setState({
            seatMapReady: true
          });
        } else {
          preselectLevel(cartItems, this.props.seatData);
        }

        // zoom and pan into the selected seat after cart is loaded
        let firstItem = head(cartItems);

        let reservedSeats = find(this.props.seatData, function(seat) {
          return seat.seat_no == firstItem.seat_no;
        });

        let seatEl = findSeatEl(reservedSeats);
        let config = get(this.props.currentFacility, 'levelSelector');

        if (seatEl) {
          panToElement(
            seatEl,
            this.state.panZoomInstance,
            get(config, 'zoomLevel', 5)
          );
        }
      },
      () => {
        this.renderNotice();

        logger.captureMessage('Failed to get cart');
        this.setState({
          currentCart: false,
          cartItems: [],
          showMessage: true
        });

        localStorage.removeItem('sessionkey');

        let url = document.location.href;
        let currentPage = url.split('?');
        window.location = currentPage[0];
      }
    );
  }

  onPanZoomInit(panZoomInstance) {
    this.setState({
      panZoomInstance: panZoomInstance
    });
  }

  setSelectedSeats(value, callback) {
    this.setState(
      {
        selectedSeats: value
      },
      callback
    );
  }

  renderNotice() {
    return (
      <Modal>
        <div className="syos-enhanced-notice">
          <div className="syos-enhanced-notice__inner">
            <div className="syos-enhanced-notice__message">
              <p>
                There are no items in the cart or the cart is expired.
                Redirecting to Level Selector.
              </p>
            </div>
          </div>
        </div>
      </Modal>
    );
  }

  render() {
    const mode = this.getMode();

    return (
      <div
        className={classnames(
          'syos',
          `syos--facility-${get(
            this.props.currentFacility,
            'facility_no',
            'unknown'
          )}`
        )}
      >
        {this.state.showMessage ? this.renderNotice() : null};
        <div className="syos__body">
          <div className="syos__canvas">
            {this.state.svgData ? (
              <SeatMapContainer
                usesGeneratedSeatmap={this.props.usesGeneratedSeatmap}
                seatMapReady={this.state.seatMapReady}
                svgData={this.state.svgData}
                seatData={this.props.seatData}
                isEnhanced={this.state.isEnhanced}
                onPanZoomInit={this.onPanZoomInit}
                onSeatMapReady={this.onSeatMapReady}
                colours={this.props.colours}
                currentPerformanceId={this.state.currentPerformanceId}
                currentPackageId={this.props.packageNo}
                prices={this.props.prices}
                updateContext={this.props.updateContext}
                handleRelease={this.handleRelease}
                selectedSeats={this.state.selectedSeats}
              />
            ) : null}

            {this.state.currentCart && features.isEnabled('expirationTimer') ? (
              <ExpirationTimer cart={this.state.currentCart} />
            ) : null}

            {!!this.props.prices.length &&
            !!this.props.seatData.length &&
            this.state.currentCartItems !== null ? (
              <DetailsContainer
                mode={mode}
                onComplete={this.props.onComplete}
                prices={this.props.prices}
                seatData={this.props.seatData}
                seatViews={this.state.seatViews}
                maxTickets={this.state.maxTickets}
                currentCart={this.state.currentCart}
                currentCartItems={this.state.currentCartItems}
                currentFixedPackage={this.state.currentFixedPackage}
                isFixedPackagePerformance={this.state.isFixedPackagePerformance}
                performances={this.state.performances}
                currentPerformanceId={this.state.currentPerformanceId}
                currentPackageNo={this.props.packageNo}
                loginInfo={this.props.loginInfo}
                activePromoCode={this.props.activePromoCode}
                mainPanZoomInstance={this.state.panZoomInstance}
                onChangePerformance={this.onChangePerformance}
                details={this.props.details}
                priceTypes={this.props.priceTypes}
                selectedSeats={this.state.selectedSeats}
                setSelectedSeats={this.setSelectedSeats}
                updategroupSalesNotice={this.props.updategroupSalesNotice}
                renderGroupSalesNotice={this.props.renderGroupSalesNotice}
                zoneNames={this.props.zoneNames}
                handleRelease={this.handleRelease}
              />
            ) : null}
          </div>
          <ReactToolTip id="seat-detail" html={true} />
        </div>
      </div>
    );
  }
}

SYOSContainer.propTypes = {
  initialPerformanceId: PropTypes.number.isRequired,
  packageNo: PropTypes.number,
  maxTickets: PropTypes.number,
  loginInfo: PropTypes.object.isRequired,
  details: PropTypes.object,
  prices: PropTypes.array,
  seatData: PropTypes.array,
  screenData: PropTypes.array,
  screenZoneData: PropTypes.object,
  selectedLevel: PropTypes.object,
  currentFacility: PropTypes.object,
  usesGeneratedSeatmap: PropTypes.bool,
  activePromoCode: PropTypes.string,
  onChangePerformance: PropTypes.func.isRequired,
  onPromoCodeReady: PropTypes.func.isRequired,
  onComplete: PropTypes.func.isRequired,
  priceTypes: PropTypes.array,
  colours: PropTypes.array,
  updateContext: PropTypes.func,
  handleRelease: PropTypes.func,
  updategroupSalesNotice: PropTypes.func,
  renderGroupSalesNotice: PropTypes.func,
  zoneNames: PropTypes.object,
  getCartPromise: PropTypes.any
};

export default SYOSContainer;



// WEBPACK FOOTER //
// ./src/javascripts/containers/SYOSContainer.js